import * as React from 'react';
import {cn} from '../../lib/cn';
import {HelpTooltip} from '../../help-tooltip';

type FieldsetProps = {
  isActivated: boolean;
  isFocused: boolean;
  label?: string | React.ReactNode | null;
  legend?: string | React.ReactNode;
  tooltip?: React.ReactNode;
  isEmpty?: boolean;
  readOnly?: boolean;
  invalid?: boolean;
  loading?: boolean;
  disabled?: boolean;
  htmlFor?: string;
  labelId?: string;
  onClick?: () => void;
  pointerCursor?: boolean;
  className?: string;
  labelClassName?: string;
};

function Fieldset({
  isActivated,
  isFocused,
  isEmpty,
  invalid,
  label,
  tooltip,
  legend,
  onClick,
  htmlFor,
  labelId,
  readOnly,
  loading,
  disabled,
  pointerCursor,
  className,
  labelClassName,
}: FieldsetProps) {
  const showLegendText = Boolean(legend || typeof label === 'string');
  const raised = !isEmpty || isFocused;

  return (
    <>
      <div
        onClick={onClick}
        className={cn(
          'absolute box-border inline-flex max-w-full items-center pl-[3px] pr-5 transition-all duration-100 ease-in',
          pointerCursor ? 'cursor-pointer' : 'cursor-text',
          'left-[13px] text-[var(--chekin-color-gray-1)]',
          isEmpty && !isFocused
            ? 'top-[var(--field-label-top)]'
            : 'top-[var(--field-label-raised-top)] !px-1',
          isFocused && 'text-[var(--chekin-color-brand-blue)]',
          raised && invalid && 'text-[var(--error-message-color)]',
          readOnly && 'cursor-default',
          disabled && 'pointer-events-none cursor-not-allowed',
          loading && 'cursor-progress',
          labelClassName,
        )}
      >
        <label
          id={labelId}
          htmlFor={htmlFor}
          className={cn(
            'block cursor-[inherit] truncate transition-all duration-100 ease-in',
            raised
              ? 'text-[length:var(--field-label-raised-font-size)] font-[var(--field-label-raised-font-weight)]'
              : 'text-[length:var(--field-label-font-size)] font-[var(--field-label-font-weight)]',
          )}
        >
          {label}
        </label>
        {tooltip && (
          <span className="ml-1 inline-flex">
            <HelpTooltip
              content={tooltip}
              size={16}
              className={cn(isFocused && 'text-[var(--chekin-color-brand-blue)]')}
            />
          </span>
        )}
      </div>
      <fieldset
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -top-[5px] bottom-0 left-0 right-0 m-0 min-w-0 rounded-[6px] border px-[13px] text-left transition-colors duration-75',
          'border-[var(--field-border)]',
          isActivated && 'border-[var(--field-border-activated)]',
          isFocused && 'border-[var(--field-border-focused)]',
          invalid && 'border-[var(--field-border-invalid)]',
          className,
        )}
      >
        <legend
          className={cn(
            'invisible float-none block h-[11px] max-w-[0.01px] whitespace-nowrap p-0 text-[0.75em]',
            'transition-[max-width] duration-[50ms] ease-out',
            raised && 'max-w-full !duration-100 !delay-[50ms]',
            !label && 'w-0',
          )}
        >
          {showLegendText && (
            <span
              className={cn(
                'visible inline-block px-1 text-[length:var(--field-label-raised-font-size)] opacity-0',
                raised
                  ? 'font-[var(--field-label-raised-font-weight)]'
                  : 'font-[var(--field-label-font-weight)]',
              )}
            >
              {legend || label}
            </span>
          )}
          {tooltip && (
            <span className="visible inline-block w-[20px] opacity-0">
              <span className="inline-block h-4 w-4" />
            </span>
          )}
        </legend>
      </fieldset>
    </>
  );
}

export {Fieldset};
export type {FieldsetProps};
